import { useCallback, useContext, useEffect, useState } from 'react';
import { AccessibleLottie } from '@/components/AccessibleLottie';
import { cn } from '@/lib/utils';
import { ThemeContext } from '@/contexts/ThemeContext/ThemeContext';
import {
    ZIGBOT_ATTENTION_PLAY_COUNT,
    loadZigBotAttention,
} from '@/components/ZigBot/zigBotIconAssets';

// Covers the unmount so the overlay leaves on a blend rather than a cut.
const FADE_OUT_MS = 600;

export interface ZigBotAttentionGlowProps {
    onFinished?: () => void;
}

/**
 * The renderer plays every sweep in one run rather than this remounting between them. A remount
 * re-parses the JSON, which showed as the next sweep starting cut off. No static fallback either,
 * since the button's own background is already this animation's resting state.
 */
export const ZigBotAttentionGlow = ({ onFinished }: ZigBotAttentionGlowProps) => {
    // Read the context directly rather than via useTheme(), which throws outside a ThemeProvider.
    const theme = useContext(ThemeContext)?.theme ?? 'light';
    const [animationData, setAnimationData] = useState<unknown | null>(null);
    const [isFadingOut, setIsFadingOut] = useState(false);

    useEffect(() => {
        let cancelled = false;
        loadZigBotAttention(theme).then((data) => {
            if (!cancelled) {
                setAnimationData(data);
            }
        });
        return () => {
            cancelled = true;
        };
    }, [theme]);

    const onSweepsComplete = useCallback(() => setIsFadingOut(true), []);

    useEffect(() => {
        if (!isFadingOut) {
            return;
        }
        // Reported only once the overlay has gone so the caller does not unmount it mid-fade.
        const doneTimer = setTimeout(() => onFinished?.(), FADE_OUT_MS);
        return () => clearTimeout(doneTimer);
    }, [isFadingOut, onFinished]);

    if (animationData === null) {
        return null;
    }

    return (
        <span
            className={cn(
                'pointer-events-none absolute inset-0 z-0 overflow-hidden rounded-full',
                'transition-opacity ease-out',
                isFadingOut ? 'opacity-0' : 'opacity-100',
            )}
            style={{ transitionDuration: `${FADE_OUT_MS}ms` }}
        >
            <AccessibleLottie
                animationData={animationData}
                loop={false}
                playCount={ZIGBOT_ATTENTION_PLAY_COUNT}
                onComplete={onSweepsComplete}
                alt=""
                presentational
            />
        </span>
    );
};
